/**
 * OMROKKERING AV EN INNSTILLINGSLISTE — ÉN KONTRAKT, FOR HVER LISTE SOM KAN DRAS (sak 191).
 *
 * ------------------------------------------------------------------------------------------------
 * HVORFOR DETTE ER ÉN KONTRAKT OG IKKE TO, samme argument som `setup-packs.ts`.
 *
 * To lister kan dras i dag — akkrediteringstypene og scenetidene — og spørsmålet er identisk:
 * «denne årgangens liste, i denne rekkefølgen». To nesten like skjemaer ville vært to steder å
 * glemme `editionId` den dagen en tredje liste får et håndtak.
 *
 * ------------------------------------------------------------------------------------------------
 * ÉN FORESPØRSEL, IKKE ÉN PER RAD — OG DET ER MÅLT.
 *
 * Klienten gjorde `for (const write of renumbered(...)) update.mutate(...)`. Å dra den øverste av
 * sju rader ga SJU PATCH-er, sendt samtidig, uten rekkefølgegaranti, og sju invalideringer av
 * programoversikten etterpå. Feilet nummer fire, sto lista halvt omnummerert — en tilstand ingen
 * hadde bedt om og ingen fikk beskjed om.
 *
 * Formen er den `reorderPerformancesRequestSchema` allerede har, og begrunnelsen der gjelder ord for
 * ord her: klienten sender ID-ene i den rekkefølgen de nå står, og serveren skriver `sort_order` ut
 * fra posisjonen i lista. Alternativet er et tall MELLOM to naboer, og mellomrommene tar slutt.
 *
 * HELE LISTA MÅ SENDES. En delvis liste ville latt radene den utelot beholde plassene de sendte
 * radene nå krever, så rekkefølgen etter skrivingen ikke var den man så. Begge listene er små av
 * kontrakt — `MAX_ACCREDITATION_TYPES` og `MAX_STAGE_TIME_KINDS` er 30 hver — og hentes i én
 * spørring uten paginering, så klienten HAR hele lista når den drar i den.
 *
 * ------------------------------------------------------------------------------------------------
 * SVARET ER ET TALL. Den som drar holder lista allerede, så et helt svar ville vært en andre kopi
 * av det som står på skjermen. `movedCount` er `UPDATE`-ens eget radtall: et drag som endte der det
 * startet skriver ingenting og sier 0.
 */
import { z } from 'zod';

/**
 * Taket på én omrokkering.
 *
 * Det er bevisst høyere enn de 30 hver av listene tåler: taket her verner mot en forespørsel som
 * ikke ligner noe skjermbilde, mens `MAX_ACCREDITATION_TYPES` og `MAX_STAGE_TIME_KINDS` er reglene
 * for hvor lange listene får bli. To tak som betyr to forskjellige ting.
 */
export const MAX_REORDERED_SETTINGS_ROWS = 100;

/**
 * Mellomrommet mellom to naboer etter en omrokkering.
 *
 * Ti og ikke én, som resten av produktet: det gir plass til å skyte noe inn mellom to rader uten å
 * røre nabolaget, og det er den samme trappa standardpakkene bruker (migrering 0300). Klientens
 * «legg til» regner `(siste sortOrder) + 10` og lander dermed fortsatt etter den nederste raden.
 */
export const REORDER_SETTINGS_STEP = 10;

export const reorderSettingsRequestSchema = z
  .object({
    editionId: z.uuid(),
    /** Hele årgangens liste, i den rekkefølgen den nå står. Posisjonen blir `sortOrder`. */
    ids: z.array(z.uuid()).min(1).max(MAX_REORDERED_SETTINGS_ROWS),
  })
  .refine((body) => new Set(body.ids).size === body.ids.length, {
    message: 'The same row cannot appear twice.',
    path: ['ids'],
  })
  .meta({ id: 'ReorderSettingsRequest' });
export type ReorderSettingsRequest = z.infer<typeof reorderSettingsRequestSchema>;

export const reorderSettingsResponseSchema = z
  .object({ movedCount: z.number().int().min(0) })
  .meta({ id: 'ReorderSettingsResponse' });
export type ReorderSettingsResponse = z.infer<typeof reorderSettingsResponseSchema>;
